import type { Room } from 'livekit-client'
import { useEffect } from 'react'

/**
 * Ask before the tab is closed mid-class.
 *
 * In-app leaving got a real confirmation dialog (see ConfirmDialog) because a
 * child two taps from ending a paid lesson deserves an explicit yes. The other
 * road out of a lesson — closing the tab, Cmd+W, navigating away — had no
 * guard at all, and it is the EASIER mistake to make: one keystroke, no undo.
 *
 * This cannot be our ConfirmDialog. Browsers stopped letting pages draw
 * custom UI (or custom text) on unload years ago; the ONLY sanctioned tool is
 * `beforeunload` + preventDefault, which shows the browser's own generic
 * "Leave site?" confirm. Native prompt, our timing — that is the whole deal
 * on offer, and it is still worth taking: cancel keeps the lesson alive,
 * confirm falls through to the normal unload path (the chat outbox flush and
 * PiP teardown both listen on `pagehide`, which fires only after a confirm).
 *
 * This hook also owns the REAL-exit disconnect. livekit-client's own
 * `disconnectOnPageLeave` is switched off (room-options.ts) because it hooks
 * `beforeunload` too — i.e. it would kill the room the instant the prompt
 * OPENED, turning Cancel into "keep the tab, lose the class". The equivalent
 * crisp departure lives here instead, on `pagehide` alone: pagehide fires only
 * when the page is genuinely going away (close confirmed, navigation, bfcache
 * entry), never for a canceled prompt — so the other side sees a clean leave
 * instead of a participant that lingers until the server times them out.
 *
 * Scoped to the Classroom mount on purpose: once the student has left, been
 * evicted, or the class has moved to another calling app, Classroom unmounts, the listeners
 * go with them, and closing the tab from those screens stays free. A refresh
 * (F5) also triggers the prompt — accepted; the reconnect credential survives
 * in localStorage and every serious meeting product behaves this way.
 *
 * Browsers only honour beforeunload after the user has interacted with the
 * page ("sticky activation") — always true here, joining takes a click.
 * iOS Safari fires beforeunload unreliably; there the guard simply does not
 * prompt and pagehide still handles the disconnect — a no-op, not a break.
 */
export function useTabCloseGuard(room: Room | undefined): void {
  useEffect(() => {
    const confirmClose = (event: BeforeUnloadEvent) => {
      // preventDefault is the spec'd trigger; assigning returnValue is what
      // older Chromium (our floor is 84) actually listens for. Set both.
      event.preventDefault()
      event.returnValue = ''
    }

    // The page is really going away — leave crisply so nobody stares at a
    // frozen tile for fifteen seconds of server timeout. Fire-and-forget: the
    // document is being torn down, there is nothing to await into.
    const disconnectOnExit = (event: PageTransitionEvent) => {
      // bfcache / app-switch: the page may come back. Disconnecting here
      // turns a resume into a cold rejoin (Reconnect: false).
      if (event.persisted) return
      void room?.disconnect()
    }

    window.addEventListener('beforeunload', confirmClose)
    window.addEventListener('pagehide', disconnectOnExit)
    return () => {
      window.removeEventListener('beforeunload', confirmClose)
      window.removeEventListener('pagehide', disconnectOnExit)
    }
  }, [room])
}
